import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CaretRightOutlined, CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, ConfigProvider } from 'antd';
import classNames from 'classnames';

import Alert from '@core/app/actions/alert-caller';
import { CanvasElements } from '@core/app/constants/canvasElements';
import history from '@core/app/svgedit/history/history';
import undoManager from '@core/app/svgedit/history/undoManager';
import layerManager from '@core/app/svgedit/layer/layerManager';
import selectionManager from '@core/app/svgedit/selection';
import { getBBox } from '@core/app/svgedit/utils/getBBox';
import workareaManager from '@core/app/svgedit/workarea';
import Modal from '@core/app/widgets/Modal';
import getClipperLib from '@core/helpers/clipper/getClipperLib';
import { getOS } from '@core/helpers/getOS';
import isWeb from '@core/helpers/is-web';
import requirejsHelper from '@core/helpers/requirejs-helper';
import { getSVGAsync } from '@core/helpers/svg-editor-helper';
import useI18n from '@core/helpers/useI18n';

import styles from './SvgNestButtons.module.scss';

let svgedit: any;

getSVGAsync((globalSVG) => {
  svgedit = globalSVG.Edit;
});

async function setUpSvgNest() {
  if (isWeb()) {
    await requirejsHelper('js/lib/svg-nest/svgnest');
    await requirejsHelper('js/lib/svg-nest/util/geometryutil');
    await requirejsHelper('js/lib/svg-nest/util/parallel');
  }
}

interface SvgNestButtonsProps {
  onClose: () => void;
}

const SvgNestButtons = ({ onClose }: SvgNestButtonsProps): React.JSX.Element => {
  const [isWorking, setIsWorking] = useState(false);
  const lang = useI18n().beambox.tool_panels;

  const undoNestChangesRef = useRef<any[]>([]);
  const nestedElementsRef = useRef<any[]>([]);

  useEffect(() => {
    if (!window['SvgNest' as any]) {
      setUpSvgNest();
    }
  }, []);

  const nestElements = useCallback((elements: Element[], containerElem?: HTMLElement, config?: any) => {
    let containerPoints;
    const ClipperLib = getClipperLib();

    if (containerElem) {
      const containerDPath = svgedit.utilities.getPathDFromElement(containerElem);
      const bbox = getBBox(containerElem as SVGGraphicsElement, { ignoreTransform: true });
      const rotation = {
        angle: svgedit.utilities.getRotationAngle(containerElem),
        cx: bbox.x + bbox.width / 2,
        cy: bbox.y + bbox.height / 2,
      };

      containerPoints = ClipperLib.dPathToPointPathsAndScale(containerDPath, rotation, 1);
    } else {
      const {
        boundary: { maxX, maxY, minX, minY },
      } = workareaManager;

      containerPoints = [
        { x: minX, y: minY },
        { x: maxX, y: minY },
        { x: maxX, y: maxY },
        { x: minX, y: maxY },
      ];
    }

    const elemPoints = [];

    undoNestChangesRef.current = [];
    nestedElementsRef.current = [...elements];
    for (let i = 0; i < elements.length; i++) {
      let elem = elements[i];

      if (!elem || elem.tagName === 'filter') {
        continue;
      }

      const bbox = getBBox(elem);
      const id = elem.getAttribute('id');
      const rotation = {
        angle: svgedit.utilities.getRotationAngle(elem),
        cx: bbox.x + bbox.width / 2,
        cy: bbox.y + bbox.height / 2,
      };

      let points;
      const d = svgedit.utilities.getPathDFromElement(elem);

      if (d) {
        const pointPaths = ClipperLib.dPathToPointPathsAndScale(d, rotation, 1);

        if (pointPaths.length === 1) {
          [points] = pointPaths;
        }
      }

      if (!points) {
        points = [
          { x: bbox.x, y: bbox.y },
          { x: bbox.x + bbox.width, y: bbox.y },
          { x: bbox.x + bbox.width, y: bbox.y + bbox.height },
          { x: bbox.x, y: bbox.y + bbox.height },
        ];

        if (rotation.angle) {
          const rad = rotation.angle * (Math.PI / 180);

          points = points.map((p) => {
            const x = p.x - rotation.cx;
            const y = p.y - rotation.cy;
            const newX = x * Math.cos(rad) - y * Math.sin(rad) + rotation.cx;
            const newY = y * Math.cos(rad) + x * Math.sin(rad) + rotation.cy;

            return { x: newX, y: newY };
          });
        }
      }

      points.source = id;
      points.id = elemPoints.length;

      // SvgNest does not support 2 points line segment, here is a hacking to allow the arrangement
      if (points.length === 2) {
        points.push(points[1]);
      }

      elemPoints.push(points);

      const elementsToUndo: any[] = [elem];

      while (elementsToUndo.length > 0) {
        elem = elementsToUndo.pop();

        if (elem.nodeType !== 1) {
          continue;
        }

        const undoRecord = {
          attrs: { transform: elem.getAttribute('transform') } as { [key: string]: null | string },
          element: elem,
        };

        switch (elem.tagName) {
          case 'path':
            undoRecord.attrs.d = elem.getAttribute('d');
            break;
          case 'polygon':
            undoRecord.attrs.points = elem.getAttribute('points');
            break;
          case 'ellipse':
            undoRecord.attrs.cx = elem.getAttribute('cx');
            undoRecord.attrs.cy = elem.getAttribute('cy');
            break;
          default:
            undoRecord.attrs.x = elem.getAttribute('x');
            undoRecord.attrs.y = elem.getAttribute('y');
            break;
        }

        elementsToUndo.push(...elem.childNodes);
        undoNestChangesRef.current.push(undoRecord);
      }
    }

    const SvgNest = window['SvgNest' as any] as any;

    if (config) {
      SvgNest.config(config);
    }

    SvgNest.nestElements(containerPoints, elemPoints);
  }, []);

  const stopNestElement = useCallback((): void => {
    const SvgNest = window['SvgNest' as any] as any;

    SvgNest.stop();

    const batchCmd = new history.BatchCommand('Svg Nest');

    for (const change of undoNestChangesRef.current) {
      const elem = change.element;
      const subCmd = new history.ChangeElementCommand(elem, change.attrs);

      batchCmd.addSubCommand(subCmd);
    }

    if (!batchCmd.isEmpty()) {
      undoManager.addCommandToHistory(batchCmd);
    }

    selectionManager.multiSelect(nestedElementsRef.current);
    nestedElementsRef.current = [];
  }, []);

  const close = useCallback((): void => {
    if (isWorking) {
      stopNestElement();
    }

    onClose();
  }, [isWorking, onClose, stopNestElement]);

  const onStartOrStop = useCallback((): void => {
    if (!isWorking) {
      const elems = selectionManager.getSelectedElements(true);

      selectionManager.clearSelection();

      if (elems.length === 0) {
        // Empty use all elements
        const allLayers = layerManager.getAllLayers();

        for (let i = 0; i < allLayers.length; i += 1) {
          const layerObject = allLayers[i];
          const layer = layerObject.getGroup();

          if (layer.getAttribute('display') === 'none' || layer.getAttribute('data-lock') === 'true') {
            continue;
          }

          const { children } = layer;

          for (const child of children) {
            if (!CanvasElements.defElems.includes(child.nodeName)) {
              elems.push(child as unknown as SVGElement);
            }
          }
        }
      }

      console.log(elems);

      if (elems.length === 0) {
        Alert.popUp({ caption: lang.nest, message: lang._nest.no_element });

        return;
      }

      nestElements(elems);
    } else {
      stopNestElement();
    }

    setIsWorking(!isWorking);
  }, [isWorking, nestElements, stopNestElement, lang]);

  const renderStartButton = (): React.JSX.Element => {
    const icon = isWorking ? <LoadingOutlined /> : <CaretRightOutlined />;
    const label = isWorking ? lang._nest.stop_nest : lang._nest.start_nest;

    return (
      <ConfigProvider theme={{ token: { colorPrimary: '#3875F6' } }}>
        <Button icon={icon} onClick={onStartOrStop} shape="round" type="primary">
          {label}
        </Button>
      </ConfigProvider>
    );
  };

  const isWindows = !isWeb() && getOS() === 'Windows';
  const className = classNames(styles.container, { [styles.win]: isWindows });

  const content = (
    <div className={className}>
      {renderStartButton()}
      <Button icon={<CloseOutlined />} onClick={close} shape="round">
        {lang._nest.end}
      </Button>
    </div>
  );

  return isWorking ? <Modal className={{ 'no-background': true }}>{content}</Modal> : content;
};

export default SvgNestButtons;
